import { __rest, __assign } from 'tslib';
import React, { lazy, useRef, useState, useCallback, useMemo, useEffect, Suspense } from 'react';
import clsx from 'clsx';
import { TextInputV2 } from '../TextInputV2.js';
import { isEqual, debounce } from 'lodash-es';

var ValidationMessage = lazy(function () { return import('./ValidationMessage.js'); });
var ValidatedTextInputV2 = function (_a) {
    // assign an id to each validator (when they change reassign that id)
    // based on the id we can set which validator messages we want to show
    // this is so we can send validator messages as node elements (mostly for translations)
    var _b;
    var id = _a.id, validators = _a.validators, _c = _a.validationEnabled, validationEnabled = _c === void 0 ? true : _c, initialValidation = _a.initialValidation, showOnlyOneError = _a.showOnlyOneError, forceValidate = _a.forceValidate, _d = _a.showValidationMessage, showValidationMessage = _d === void 0 ? true : _d, delay = _a.delay, _e = _a.floatMessage, floatMessage = _e === void 0 ? false : _e, onChange = _a.onChange, onBlur = _a.onBlur, onFocus = _a.onFocus, onValidate = _a.onValidate, controlledValue = _a.value, className = _a.className, inputProps = __rest(_a, ["id", "validators", "validationEnabled", "initialValidation", "showOnlyOneError", "forceValidate", "showValidationMessage", "delay", "floatMessage", "onChange", "onBlur", "onFocus", "onValidate", "value", "className"]);
    var ranValidation = useRef(false);
    var _f = useState({
        messages: [],
        numError: 0,
        numSuccess: 0
    }), validationResult = _f[0], setValidationResult = _f[1];
    var runValidation = useCallback(function (value) {
        ranValidation.current = true;
        var successValidators = [];
        var failedValidators = [];
        var numSuccess = 0;
        var numError = 0;
        var messages = [];
        validators === null || validators === void 0 ? void 0 : validators.every(function (v) {
            var _a;
            if (v !== undefined) {
                var validationResult_1 = (_a = v.validator) === null || _a === void 0 ? void 0 : _a.call(v, value);
                if (['error', false].includes(validationResult_1)) {
                    // returning 'error' is legacy. Should use boolean.
                    failedValidators.push(v);
                    if (v.errorMessage) {
                        numError += 1;
                        messages.push(__assign(__assign({}, v), { isError: true }));
                    }
                    if (showOnlyOneError) {
                        // Break out of every loop
                        return false;
                    }
                }
                else {
                    successValidators.push(v);
                    if (v.successMessage) {
                        numSuccess += 1;
                        messages.push(__assign(__assign({}, v), { isError: false }));
                    }
                }
            }
            // Continue loop
            return true;
        });
        setValidationResult(function (prev) {
            var newResult = {
                numError: numError,
                numSuccess: numSuccess,
                messages: messages
            };
            if (isEqual(prev, newResult)) {
                return prev;
            }
            onValidate === null || onValidate === void 0 ? void 0 : onValidate(failedValidators, successValidators);
            return newResult;
        });
        return !numError;
    }, [onValidate, showOnlyOneError, validators]);
    var onChangeInput = useCallback(function (v, e) {
        onChange === null || onChange === void 0 ? void 0 : onChange(v, e, validationEnabled, validationEnabled ? runValidation(v) : true);
    }, [onChange, runValidation, validationEnabled]);
    var debounceOnChange = useMemo(function () {
        return debounce(function (v, e) {
            onChangeInput === null || onChangeInput === void 0 ? void 0 : onChangeInput(v, e);
        }, delay, { leading: false, trailing: true });
    }, [delay, onChangeInput]);
    var handleOnChange = useCallback(function (v, e) {
        delay ? debounceOnChange(v, e) : onChangeInput === null || onChangeInput === void 0 ? void 0 : onChangeInput(v, e);
    }, [debounceOnChange, delay, onChangeInput]);
    var onBlurInput = useCallback(function (e) {
        var v = e.target.value;
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(v, e, validationEnabled, validationEnabled ? runValidation(v) : true);
    }, [onBlur, runValidation, validationEnabled]);
    var onFocusInput = useCallback(function (e) {
        var v = e.target.value;
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(v, e, validationEnabled, validationEnabled ? runValidation(v) : true);
    }, [onFocus, runValidation, validationEnabled]);
    var canValidateControlledValue = useMemo(function () { return validationEnabled && controlledValue !== undefined; }, [controlledValue, validationEnabled]);
    // Validate again if prop changed and validation ran at least once.
    useEffect(function () {
        if (canValidateControlledValue && ranValidation.current) {
            runValidation(controlledValue.toString());
        }
    }, [runValidation, forceValidate, canValidateControlledValue, controlledValue]);
    useEffect(function () {
        if (initialValidation && canValidateControlledValue) {
            // run validation at mount
            runValidation(controlledValue.toString());
        }
        // Note: Only run once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(function () {
        return function () {
            debounceOnChange.cancel();
        };
    }, [debounceOnChange]);
    var classPrefix = 'c-input-validation';
    return (React.createElement("div", { "data-testid": id ? "".concat(id, "-validation-wrapper") : undefined, className: clsx((_b = {},
            _b[classPrefix] = validationEnabled,
            _b["".concat(classPrefix, "--error")] = validationEnabled && !!validationResult.numError,
            _b["".concat(classPrefix, "--success")] = validationEnabled && !!validationResult.numSuccess && !validationResult.numError,
            _b), className) },
        React.createElement(TextInputV2, __assign({}, inputProps, { id: id, value: controlledValue, onChange: onChange ? handleOnChange : undefined, onBlur: onBlur ? onBlurInput : undefined, onFocus: onFocus ? onFocusInput : undefined, removeWrapper: true })),
        React.createElement(Suspense, { fallback: '' }, showValidationMessage && !!validationResult.messages.length && (React.createElement(ValidationMessage, { floatMessage: floatMessage, messages: validationResult.messages })))));
};

export { ValidatedTextInputV2 };
//# sourceMappingURL=ValidatedTextInputV2.js.map
